import { inject, Injectable } from "@angular/core";
import {
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
} from "rxjs";

import { OrganizationApiServiceAbstraction } from "@bitwarden/common/admin-console/abstractions/organization/organization-api.service.abstraction";
import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { PolicyType } from "@bitwarden/common/admin-console/enums";
import { PendingOrganizationInvitationResponse } from "@bitwarden/common/admin-console/models/response/pending-organization-invitation.response";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { OrganizationInviteService } from "@bitwarden/common/auth/organization-invite";
import { DirectOrganizationInvite } from "@bitwarden/common/auth/organization-invite/models/direct-organization-invite";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { EventCollectionService, EventType } from "@bitwarden/common/dirt/event-logs";
import { FeatureFlag } from "@bitwarden/common/enums/feature-flag.enum";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import {
  StateProvider,
  UserKeyDefinition,
  VAULT_ORG_USER_NOTIFICATION_DISK_LOCAL,
} from "@bitwarden/common/platform/state";
import { OrganizationId } from "@bitwarden/common/types/guid";

export interface OrganizationUserNotificationBannerData {
  organizationId: OrganizationId;
  header: string | null;
  description: string;
  buttonText: string | null;
  showAfterEveryLogin: boolean;
  revisionDate: Date;
}

export type PendingOrganizationInvitation = PendingOrganizationInvitationResponse;

const dateDeserializer = (value: string) => (value != null ? new Date(value) : null);

export const NOTIFICATION_BANNER_DISMISSED_KEY = new UserKeyDefinition<Date>(
  VAULT_ORG_USER_NOTIFICATION_DISK_LOCAL,
  "notificationBannerDismissed",
  {
    deserializer: dateDeserializer,
    clearOn: [],
  },
);

export const NOTIFICATION_BANNER_DISMISSED_SESSION_KEY = new UserKeyDefinition<Date>(
  VAULT_ORG_USER_NOTIFICATION_DISK_LOCAL,
  "notificationBannerDismissedSession",
  {
    deserializer: dateDeserializer,
    clearOn: ["logout"],
  },
);

@Injectable()
export class VaultOrganizationUserNotificationsService {
  private readonly accountService = inject(AccountService);
  private readonly policyService = inject(PolicyService);
  private readonly configService = inject(ConfigService);
  private readonly stateProvider = inject(StateProvider);
  private readonly eventCollectionService = inject(EventCollectionService);
  private readonly organizationApiService = inject(OrganizationApiServiceAbstraction);
  private readonly organizationInviteService = inject(OrganizationInviteService);
  private readonly pendingInvitationsRefresh$ = new Subject<void>();

  private readonly userNotificationPolicies$ = this.accountService.activeAccount$.pipe(
    getUserId,
    switchMap((userId) =>
      this.policyService.policiesByType$(PolicyType.OrganizationUserNotification, userId),
    ),
  );

  private readonly latestUserNotificationPolicy$ = this.userNotificationPolicies$.pipe(
    map((policies) =>
      policies.reduce(
        (latest, policy) =>
          latest == null || policy.revisionDate > latest.revisionDate ? policy : latest,
        null as (typeof policies)[number] | null,
      ),
    ),
  );

  readonly notificationData$ = this.latestUserNotificationPolicy$.pipe(
    map((policy) => {
      if (!policy || !policy.enabled) {
        return null;
      }
      return {
        organizationId: policy.organizationId,
        header: (policy.data?.header as string | null) ?? null,
        description: (policy.data?.description as string) ?? "",
        buttonText: (policy.data?.buttonText as string | null) ?? null,
        showAfterEveryLogin: (policy.data?.showAfterEveryLogin as boolean) ?? false,
        revisionDate: policy.revisionDate,
      } satisfies OrganizationUserNotificationBannerData;
    }),
    distinctUntilChanged((a, b) => {
      if (a === b) {
        return true;
      }
      if (a == null || b == null) {
        return false;
      }
      return (
        a.organizationId === b.organizationId &&
        a.header === b.header &&
        a.description === b.description &&
        a.buttonText === b.buttonText &&
        a.showAfterEveryLogin === b.showAfterEveryLogin &&
        a.revisionDate.getTime() === b.revisionDate.getTime()
      );
    }),
  );

  readonly showNotificationBanner$ = this.accountService.activeAccount$.pipe(
    getUserId,
    switchMap((userId) =>
      combineLatest([
        this.notificationData$,
        this.stateProvider.getUser(userId, NOTIFICATION_BANNER_DISMISSED_KEY).state$,
        this.stateProvider.getUser(userId, NOTIFICATION_BANNER_DISMISSED_SESSION_KEY).state$,
        this.configService.getFeatureFlag$(FeatureFlag.PM31948_OrgUserNotificationBanner),
      ]),
    ),
    map(([data, lastDismissedDate, lastDismissedSessionDate, featureFlagEnabled]) => {
      if (!data || !featureFlagEnabled) {
        return false;
      }

      if (lastDismissedDate == null || lastDismissedDate < data.revisionDate) {
        return true;
      }

      return data.showAfterEveryLogin && lastDismissedSessionDate == null;
    }),
  );

  readonly pendingInvitations$ = this.accountService.activeAccount$.pipe(
    switchMap((account) =>
      account == null
        ? of([])
        : this.pendingInvitationsRefresh$.pipe(
            startWith(undefined),
            switchMap(() =>
              defer(() => this.organizationApiService.getPendingInvitations()).pipe(
                map((response) => response.data),
                catchError(() => of([])),
              ),
            ),
          ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  async acceptPendingInvitation(
    invitation: PendingOrganizationInvitation,
    postAuthRedirectUrl: string,
  ): Promise<boolean> {
    const account = await firstValueFrom(this.accountService.activeAccount$);
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    const invite = new DirectOrganizationInvite({
      email: account.email,
      initOrganization: false,
      orgUserHasExistingUser: true,
      organizationId: invitation.organizationId,
      organizationName: invitation.organizationName,
      organizationUserId: invitation.organizationUserId,
      token: invitation.token,
    });

    const accepted = await this.organizationInviteService.validateAndAcceptDirectOrgInvite(
      invite,
      userId,
      postAuthRedirectUrl,
    );
    if (accepted) {
      this.pendingInvitationsRefresh$.next();
    }
    return accepted;
  }

  async saveDismissalToState(): Promise<void> {
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    const now = new Date();
    await Promise.all([
      this.stateProvider.getUser(userId, NOTIFICATION_BANNER_DISMISSED_KEY).update(() => now),
      this.stateProvider
        .getUser(userId, NOTIFICATION_BANNER_DISMISSED_SESSION_KEY)
        .update(() => now),
    ]);
  }

  async recordActionButtonClick(organizationId: OrganizationId): Promise<void> {
    await this.eventCollectionService.collect(
      EventType.OrganizationUser_NotificationBannerActionClicked,
      undefined,
      false,
      organizationId,
    );
    await this.saveDismissalToState();
  }
}
